import * as React from 'react';
import { useState } from 'react';
import { useHistory, useLocation } from 'react-router';

import { closeDialog, updateSearch } from 'bernie-core';

import { useLocalization } from '@shared-ui/localization-context';

import { DayName, UitkDatePicker, UitkDatePickerProps, UitkDatePickerRenderType } from 'uitk-react-date-picker';

import { useSearchWidgetContext } from 'features/search-widget';

const dialogId = 'dates-picker';

interface DatePickerProps {
  customStartLabel?: string;
  customEndLabel?: string;
}

function DatesPicker({ customStartLabel, customEndLabel }: DatePickerProps): JSX.Element {
  const history = useHistory();
  const location = useLocation();

  const { formatDateString, formatText } = useLocalization();

  const { startDate, endDate, setStartDate, setEndDate } = useSearchWidgetContext();

  const [showDatePicker, setShowDatePicker] = useState(false);

  function toggleDatePicker(): void {
    setShowDatePicker(!showDatePicker);
  }

  function openDatePickerDialog(): void {
    updateSearch({ history, location, newParams: { pwaDialog: dialogId } });
  }

  function closeDatePickerDialog(): void {
    closeDialog({ history, location, key: dialogId });
  }

  function routerLinkWrapper(children): JSX.Element {
    return <>{children}</>;
  }

  function onOpenCB(name, isDialog): void {
    setShowDatePicker(true);

    if (isDialog) {
      openDatePickerDialog();
    }
  }

  function onChangeStartDateCB(nextStartDate: Date): void {
    // clear end date if start date later than current end
    if (endDate && nextStartDate > endDate) {
      setEndDate(undefined);
    }

    // updating start date in form store to allow endDate CB to know it can
    // close datepicker
    setStartDate(nextStartDate);
  }

  function onChangeEndDateCB(nextEndDate: Date): void {
    setEndDate(nextEndDate);

    if (startDate) {
      toggleDatePicker();
      closeDatePickerDialog();
    }
  }

  function onClearDatesCB(): void {
    setStartDate(undefined);
    setEndDate(undefined);
  }

  function onDismissCB(isDialog, noUpdate): void {
    if (isDialog) {
      closeDatePickerDialog();
    }

    if (!noUpdate) {
      toggleDatePicker();
    }
  }

  const dateFieldProps = {
    dateFieldWrapperClasses: 'dates__picker',
    endId: 'end-id',
    endInvalid: '',
    endLabel: customEndLabel || formatText('product.search.datePicker.toDate.label'),
    endName: 'endDate',
    endPlaceholder: '',
    onChange: () => null, // error thrown if no onChange handler is passed
    routerLinkWrapper,
    showDatePicker,
    startId: 'start-id',
    startInvalid: '',
    startLabel: customStartLabel || formatText('product.search.datePicker.fromDate.label'),
    startName: 'startDate',
    startPlaceholder: '',
  };

  const datePickerProps: UitkDatePickerProps = {
    dateFieldProps,
    daysBookableInAdvance: 500,
    doubleMonths: true,
    endDate: formatDateString(endDate, { raw: 'yyyy-MM-dd' }),
    firstDayOfWeek: DayName.SUN,
    midnightBookingOffset: 4,
    onDismissCB,
    onOpenCB,
    onChangeStartDateCB,
    onChangeEndDateCB,
    onClearDatesCB,
    renderType: UitkDatePickerRenderType.DIALOG,
    singleMode: false,
    startDate: formatDateString(startDate, { raw: 'yyyy-MM-dd' }),
    useClearDates: true,
  };

  return <UitkDatePicker {...datePickerProps} />;
}

export { DatesPicker };
